import React, { useEffect, useState, useRef } from 'react';
import { MapPinIcon, AwardIcon, GlobeIcon } from 'lucide-react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
export function PartnerClinicsSection() {
  const mapRef = useRef<SVGSVGElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const clinics = [{
    name: 'Spain',
    coordinates: [2.1734, 41.3851],
    description: 'Multiple premier clinics across Spanish cities',
    image: 'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=800&auto=format&fit=crop'
  }, {
    name: 'Czech Republic',
    coordinates: [14.4378, 50.0755],
    description: 'State-of-the-art facilities in major Czech cities',
    image: 'https://images.unsplash.com/photo-1541849546-216549ae216d?w=800&auto=format&fit=crop'
  }, {
    name: 'Poland',
    coordinates: [21.0122, 52.2297],
    description: 'Leading clinics throughout Poland',
    image: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800&auto=format&fit=crop'
  }];
  useEffect(() => {
    if (!mapRef.current) return;
    const width = 800;
    const height = 600;
    d3.select(mapRef.current).selectAll('*').remove();
    const svg = d3.select(mapRef.current).attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMidYMid meet');
    const projection = d3.geoMercator().center([15, 52]).scale(650).translate([width / 2, height / 2]);
    const path = d3.geoPath().projection(projection);
    fetch('https://github.com/leakyMirror/map-of-europe/raw/refs/heads/master/TopoJSON/europe.topojson').then(response => {
      if (!response.ok) {
        throw new Error('Failed to load map data');
      }
      return response.json();
    }).then(topology => {
      const countries = topojson.feature(topology, topology.objects.europe);
      svg.append('g').selectAll('path').data(countries.features).enter().append('path').attr('d', path as any).attr('fill', '#e0f2fe').attr('stroke', '#cbd5e1').attr('stroke-width', 0.5).attr('class', 'country');
      const markersGroup = svg.append('g');
      clinics.forEach(clinic => {
        const [x, y] = projection(clinic.coordinates) || [0, 0];
        markersGroup.append('circle').attr('cx', x).attr('cy', y).attr('r', 20).attr('fill', '#3b82f6').attr('opacity', 0.2);
        markersGroup.append('circle').attr('cx', x).attr('cy', y).attr('r', 12).attr('fill', '#3b82f6').attr('stroke', 'white').attr('stroke-width', 2);
        markersGroup.append('circle').attr('cx', x).attr('cy', y - 2).attr('r', 4).attr('fill', 'white');
      });
      setMapLoaded(true);
      setMapError(false);
    }).catch(error => {
      console.error('Error loading map:', error);
      setMapError(true);
      setMapLoaded(false);
    });
  }, []);
  return <section id="clinics" className="py-20 bg-gradient-to-b from-slate-50 to-white" data-id="element-527">
      <div className="container mx-auto px-4" data-id="element-528">
        <div className="text-center mb-16" data-id="element-529">
          <div className="inline-block bg-blue-50 rounded-full px-4 py-2 mb-4" data-id="element-530">
            <span className="text-blue-700 font-semibold text-sm" data-id="element-531">
              Partner Clinics
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-532">
            Premier <span className="text-blue-600" data-id="element-533">European Clinics</span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg" data-id="element-534">
            We've partnered with top-rated fertility centers across Europe, each
            carefully selected for their excellence, high success rates, and
            comprehensive treatment options.
          </p>
        </div>
        <div className="max-w-5xl mx-auto mb-12" data-id="element-535">
          {mapError ? <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-536">
              {clinics.map((clinic, index) => <div key={index} className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 hover:shadow-2xl transition-all" data-id="element-537">
                  <div className="h-48 overflow-hidden" data-id="element-538">
                    <img src={clinic.image} alt={clinic.name} className="w-full h-full object-cover" data-id="element-539" />
                  </div>
                  <div className="p-6" data-id="element-540">
                    <div className="flex items-center gap-2 mb-3" data-id="element-541">
                      <div className="bg-blue-500 p-2 rounded-lg" data-id="element-542">
                        <MapPinIcon className="text-white" size={20} data-id="element-543" />
                      </div>
                      <div data-id="element-544">
                        <h3 className="font-bold text-slate-900 text-xl" data-id="element-545">
                          {clinic.name}
                        </h3>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm" data-id="element-546">
                      {clinic.description}
                    </p>
                  </div>
                </div>)}
            </div> : <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 p-4 md:p-8" data-id="element-547">
              <svg ref={mapRef} className="w-full h-auto" data-id="element-548"></svg>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 pt-8 border-t border-slate-100" data-id="element-549">
                {clinics.map((clinic, index) => <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl hover:bg-blue-50 transition-colors" data-id="element-550">
                    <div className="bg-blue-500 p-2 rounded-lg" data-id="element-551">
                      <MapPinIcon className="text-white" size={20} data-id="element-552" />
                    </div>
                    <div data-id="element-553">
                      <p className="font-semibold text-slate-900" data-id="element-554">
                        {clinic.name}
                      </p>
                      <p className="text-sm text-slate-600" data-id="element-555">
                        {clinic.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-556">
          <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow" data-id="element-557">
            <div className="inline-flex bg-blue-50 p-4 rounded-2xl mb-4" data-id="element-558">
              <AwardIcon className="text-blue-600" size={32} data-id="element-559" />
            </div>
            <h3 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-560">
              Up to 90%
            </h3>
            <p className="text-slate-600" data-id="element-561">
              Success Rate with Personalized Plans
            </p>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow" data-id="element-562">
            <div className="inline-flex bg-cyan-50 p-4 rounded-2xl mb-4" data-id="element-563">
              <GlobeIcon className="text-cyan-600" size={32} data-id="element-564" />
            </div>
            <h3 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-565">
              No Waiting
            </h3>
            <p className="text-slate-600" data-id="element-566">Start Treatment Immediately</p>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow" data-id="element-567">
            <div className="inline-flex bg-blue-50 p-4 rounded-2xl mb-4" data-id="element-568">
              <MapPinIcon className="text-blue-600" size={32} data-id="element-569" />
            </div>
            <h3 className="text-3xl font-bold text-slate-900 mb-2" data-id="element-570">
              Advanced Labs
            </h3>
            <p className="text-slate-600" data-id="element-571">Latest Technology & Procedures</p>
          </div>
        </div>
        <div className="mt-12 text-center max-w-3xl mx-auto" data-id="element-572">
          <p className="text-slate-600 text-lg mb-6" data-id="element-573">
            Our partner clinics feature state-of-the-art laboratories with
            experienced embryologists using cutting-edge technology. Each clinic
            offers comprehensive donor programs and continuously improves
            procedures to maximize your chances of conception. With no waiting
            lists, you can begin your personalized treatment plan immediately.
          </p>
          <a href="/packages" className="inline-block bg-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-blue-600/30" data-id="element-574">
            View Treatment Packages
          </a>
        </div>
      </div>
    </section>;
}